import { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/apiClient';

export function useBudget(year) {
  const [budget, setBudget] = useState({ revenue_budget: 0, job_count_budget: 0, lead_budget: 0, opportunity_budget: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/forecast/budget/${year}`);
      setBudget(res.data.budget || { revenue_budget: 0, job_count_budget: 0, lead_budget: 0, opportunity_budget: 0 });
    } catch (e) {
      // no budget set yet
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const save = async (data) => {
    await apiClient.put(`/forecast/budget/${year}`, data);
    await load();
  };

  return { budget, loading, reload: load, save };
}

export function useMonthlyForecast(year) {
  const [months, setMonths] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!year) return;
    setLoading(true); setError('');
    try {
      const [mRes, sRes] = await Promise.all([
        apiClient.get(`/forecast/monthly/${year}`),
        apiClient.get(`/forecast/summary/${year}`),
      ]);
      setMonths(mRes.data.months || []);
      setSummary(sRes.data || null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load forecast');
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const updateMonth = async (month, data) => {
    await apiClient.put(`/forecast/monthly/${year}/${month}`, data);
    await load();
  };

  return { months, summary, loading, error, reload: load, updateMonth };
}

export function useLiveActuals(year) {
  const [data, setData] = useState([]);
  const [byBu, setByBu] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!year) return;
    let cancelled = false;
    setLoading(true); setError(null);
    // apiClient has no default timeout (axios waits forever otherwise), and this endpoint has
    // a real history of hanging for minutes (heavy DB scan + GHL calls) with no server error
    // at all - confirmed live as a silent multi-minute spinner with nothing in the browser
    // console. A client-side timeout means a slow backend fails visibly (a real error message)
    // instead of leaving the page stuck on "Loading live data..." forever with no signal.
    apiClient.get(`/forecast/live-actuals/${year}`, { timeout: 45000 })
      .then(res => {
        if (!cancelled) {
          setData(res.data.months || []);
          setByBu(res.data.byBu || {});
        }
      })
      .catch(err => {
        if (cancelled) return;
        const msg = err.code === 'ECONNABORTED'
          ? 'Live data is taking too long to load (over 45s) - try again in a moment.'
          : (err.response?.data?.error || 'Failed to load live data');
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year]);

  return { data, loading, error, byBu };
}

export function useWorkingDays(fiscalYear) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!fiscalYear) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/win-the-day/working-days/${fiscalYear}`);
      const map = {};
      for (const row of (res.data.workingDays || [])) map[row.month] = row.working_days;
      setData(map);
    } catch {} finally { setLoading(false); }
  }, [fiscalYear]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, reload: load };
}

export function useDepartments() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/forecast/departments');
      setDepartments(res.data.departments || []);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => { await apiClient.post('/forecast/departments', data); await load(); };
  const update = async (id, data) => { await apiClient.put(`/forecast/departments/${id}`, data); await load(); };

  return { departments, loading, reload: load, create, update };
}

export function useBudgetItems(fiscalYear) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!fiscalYear) return;
    setLoading(true); setError(null);
    try {
      const res = await apiClient.get(`/forecast/budget-items/${fiscalYear}`);
      setItems(res.data.items || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load budget items');
    } finally { setLoading(false); }
  }, [fiscalYear]);

  useEffect(() => { load(); }, [load]);

  const addItem = async (data) => {
    await apiClient.post('/forecast/budget-items', { ...data, fiscal_year: fiscalYear });
    await load();
  };

  const updateItem = async (id, data) => {
    await apiClient.put(`/forecast/budget-items/${id}`, data);
    await load();
  };

  const bulkSave = async (itemsData) => {
    await apiClient.put(`/forecast/budget-items/bulk/${fiscalYear}`, { items: itemsData });
    await load();
  };

  const deleteItem = async (id) => {
    await apiClient.delete(`/forecast/budget-items/${id}`);
    await load();
  };

  return { items, loading, error, reload: load, addItem, updateItem, bulkSave, deleteItem };
}

export function useKpis(year) {
  const [kpis, setKpis] = useState([]);
  const [actuals, setActuals] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    try {
      const [kRes, aRes] = await Promise.all([
        apiClient.get('/forecast/kpis'),
        apiClient.get(`/forecast/kpis/actuals/${year}`),
      ]);
      setKpis(kRes.data.kpis || []);
      const map = {};
      for (const row of (aRes.data.actuals || [])) {
        if (!map[row.kpi_id]) map[row.kpi_id] = {};
        map[row.kpi_id][row.month] = row.actual_value;
      }
      setActuals(map);
    } catch (e) {
      // ignore
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const createKpi = async (data) => { await apiClient.post('/forecast/kpis', data); await load(); };
  const updateKpi = async (id, data) => { await apiClient.put(`/forecast/kpis/${id}`, data); await load(); };
  const deleteKpi = async (id) => { await apiClient.delete(`/forecast/kpis/${id}`); await load(); };
  const setActual = async (kpiId, month, value) => {
    await apiClient.put(`/forecast/kpis/${kpiId}/actuals/${year}/${month}`, { actual_value: value });
    await load();
  };

  return { kpis, actuals, loading, reload: load, createKpi, updateKpi, deleteKpi, setActual };
}
